import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const optimizeCloudinary = (url) => {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", "/upload/w_800,c_limit,f_auto,q_auto/");
};


const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const filters = ['All', 'Residential', 'Commercial', 'Bathrooms', 'Kitchens', 'Offices'];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery images:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredImages =
    activeFilter === 'All'
      ? images
      : images.filter((img) => img.category === activeFilter);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section data-testid="gallery-hero" className="py-16 md:py-24 bg-gradient-to-br from-secondary/30 to-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6">
            Our Work Gallery
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            See the transformations we've achieved for homes and businesses across Perth.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section data-testid="gallery-filters" className="py-8 sticky top-20 z-40 bg-white border-b border-border">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <Button
                key={filter}
                data-testid={`filter-${filter.toLowerCase()}`}
                onClick={() => setActiveFilter(filter)}
                className={`rounded-full px-6 py-2 transition-all ${
                  activeFilter === filter
                    ? 'bg-primary text-white'
                    : 'bg-secondary text-primary hover:bg-secondary/80'
                }`}
              >
                {filter}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section data-testid="gallery-grid" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No images available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  data-testid={`gallery-item-${index}`}
                  className="relative overflow-hidden rounded-xl aspect-square group cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                     src={optimizeCloudinary(image.image_url)}
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-sm font-medium">{image.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-4 right-4 text-white hover:text-white/70 transition-colors"
            onClick={() => setSelectedImage(null)}
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>
          <img
            src={optimizeCloudinary(selectedImage.image_url)}
            alt={selectedImage.title}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />

        </div>
      )}
    </div>
  );
};

export default Gallery;
